/**
 * To field for new Create Task modal.
 * Picklist, type-ahead, single-select per action; multiple recipients as chips.
 * Adding a user adds a chip; X on chip removes. When opened (e.g. from Placement),
 * shows Key Contacts/Employee List; type-ahead searches all employees.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete, Avatar, Box, Chip, TextField, Tooltip, Typography } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { trackPromise, usePromiseTracker } from 'react-promise-tracker';
import { useDebounce } from 'utils/customHooks/useDebounce';
import { DisableAppScroll, EnableAppScroll } from 'app/layout/Layout';
import { getInitials } from 'app/components/Tasks/CreateTask/FormatData';
import { getAllEmployees } from 'app/services/TaskServices/TaskServices';
import { GenerateAllEmployeeObject } from 'app/components/Tasks/CreateTask/FormatData';
import { TaskEntity } from 'app/models/Tasks/Tasks';

export interface IToRecipient {
  object: { empId: number; label: string; role?: string; isInactive?: boolean };
  label: string;
}

export interface ToFieldProps {
  value: IToRecipient[];
  onChange: (recipients: IToRecipient[]) => void;
  placementEmployees: IToRecipient[];
  container: number;
  /** When set (e.g. for CC field), these empIds are excluded from the options list */
  excludeEmpIds?: number[];
  disabled?: boolean;
  className?: string;
  displayClassName?: string;
  placeholder?: string;
}

const useStyles = makeStyles()(theme => ({
  root: {
    width: '100%',
  },
  inputRoot: {
    minHeight: 40,
    padding: '8px 12px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 0,
    border: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxSizing: 'border-box',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px 0',
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    },
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  chip: {
    margin: '2px 6px 2px 0',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 16,
    fontSize: 13,
    color: theme.palette.text.primary,
    fontWeight: 400,
    height: 28,
    '& .MuiChip-label': {
      padding: "10px"
    },
    '& .MuiChip-deleteIcon': {
      marginLeft: 4,
      marginRight: 4,
      width: 18,
      height: 18,
      borderRadius: '50%',
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.grey[500],
      '&:hover': {
        backgroundColor: theme.palette.grey[400],
        color: theme.palette.grey[800],
      },
    },
  },
  chipInactive: {
    backgroundColor: '#FDECEA !important',
    border: 'none !important',
    color: `${theme.palette.error.main} !important`,
    '& .MuiChip-deleteIcon': {
      backgroundColor: `${theme.palette.common.white} !important`,
      color: `${theme.palette.error.main} !important`,
      borderRadius: '50%',
      width: '18px !important',
      height: '18px !important',
      '&:hover': {
        backgroundColor: `${theme.palette.common.white} !important`,
        color: `${theme.palette.error.dark} !important`,
      },
    },
  },
  inactiveTooltip: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    fontSize: 12,
    border: `1px solid ${theme.palette.grey[300]}`,
    padding: '6px 12px',
    whiteSpace: 'nowrap',
    maxWidth: 'none',
  },
  inactiveTooltipArrow: {
    color: theme.palette.common.white,
    '&::before': {
      border: `1px solid ${theme.palette.grey[300]}`,
    },
  },
  input: {
    flex: 1,
    minWidth: 140,
    '& input': {
      padding: '4px 0',
      fontSize: 14,
    },
  },
  listbox: {
    maxHeight: 320,
    padding: 0,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[4],
    borderRadius: '4px 4px 0 0',
    borderTop: `3px solid ${theme.palette.primary.main}`,
    '& .MuiAutocomplete-option': {
      padding: '10px 12px',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
  option: {
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  optionAvatar: {
    width: 32,
    height: 32,
    flexShrink: 0,
    fontSize: '12px',
    fontWeight: 600,
    backgroundColor: theme.palette.grey[600],
    color: theme.palette.common.white,
  },
  optionName: {
    fontWeight: 500,
    fontSize: 14,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  optionNameInactive: {
    color: theme.palette.error.main,
  },
  optionRole: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: 400,
    lineHeight: 1.4,
    marginTop: 2,
    display: 'block',
  },
  optionTextBlock: {
    display: 'block',
    minWidth: 0,
  },
}));

export const ToField = ({
  value,
  onChange,
  placementEmployees,
  container,
  excludeEmpIds,
  disabled = false,
  className,
  displayClassName,
  placeholder = 'Add recipients',
}: ToFieldProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [keyword, , setKeyword] = useDebounce<string>('', 500);
  const [employeeOptions, setEmployeeOptions] = useState<IToRecipient[]>([]);
  const { promiseInProgress } = usePromiseTracker({ area: 'create-task-to-field', delay: 0 });
  const isPlacement = container === TaskEntity.PLACEMENTS;

  const withInactiveMetadata = useCallback((candidates: IToRecipient[]) => {
    return (candidates || []).map(candidate => {
      const role = candidate?.object?.role || '';
      return {
        ...candidate,
        object: {
          ...candidate.object,
          isInactive: candidate?.object?.isInactive || role.toLowerCase().includes('inactive'),
        },
      };
    });
  }, []);

  const getFilteredOptions = useCallback(
    (candidates: IToRecipient[]) => {
      const selectedIds = new Set((value ?? []).map(r => r?.object?.empId).filter(Boolean));
      const excludeSet = new Set(excludeEmpIds ?? []);
      return candidates.filter(
        c =>
          c?.object?.empId != null &&
          !selectedIds.has(c.object.empId) &&
          !excludeSet.has(c.object.empId),
      );
    },
    [value, excludeEmpIds],
  );

  const fetchEmployees = useCallback(
    async (searchKey: string) => {
      if (searchKey && searchKey.length > 2) {
        const data = await getAllEmployees(searchKey);
        const formatted = withInactiveMetadata(GenerateAllEmployeeObject(data || []) as IToRecipient[]);
        setEmployeeOptions(getFilteredOptions(formatted));
      } else {
        const base =
          isPlacement && placementEmployees?.length > 0 ? withInactiveMetadata(placementEmployees) : [];
        setEmployeeOptions(getFilteredOptions(base));
      }
    },
    [isPlacement, placementEmployees, getFilteredOptions, withInactiveMetadata],
  );

  useEffect(() => {
    trackPromise(fetchEmployees(keyword), 'create-task-to-field');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  useEffect(() => {
    if (isPlacement && placementEmployees?.length > 0) {
      setEmployeeOptions(getFilteredOptions(withInactiveMetadata(placementEmployees)));
    }
  }, [placementEmployees, isPlacement, getFilteredOptions, withInactiveMetadata]);

  const handleChange = (_e: React.SyntheticEvent, newValue: IToRecipient[]) => {
    onChange(newValue ?? []);
  };

  const handleRemoveChip = (empId: number) => {
    onChange((value ?? []).filter(r => r?.object?.empId !== empId));
  };

  return (
    <div className={className ?? classes.root}>
      <Autocomplete<IToRecipient, true, true, false>
        multiple
        filterSelectedOptions
        disableClearable
        value={value ?? []}
        options={employeeOptions}
        getOptionLabel={option => option?.object?.label ?? ''}
        isOptionEqualToValue={(a, b) => a?.object?.empId === b?.object?.empId}
        loading={promiseInProgress}
        disabled={disabled}
        openOnFocus={isPlacement}
        onOpen={() => isPlacement && DisableAppScroll()}
        onClose={EnableAppScroll}
        popupIcon={null}
        noOptionsText={t('notification.createTask.noResultLabel') ?? undefined}
        filterOptions={opts => opts}
        onChange={handleChange}
        onInputChange={(_e, v) => setKeyword(v)}
        data-testid="create-task-new-to-field"
        classes={{
          listbox: classes.listbox,
        }}
        renderOption={(props, option) => (
          <li {...props} className={classes.option}>
            <Avatar className={classes.optionAvatar}>{getInitials(option?.object?.label)}</Avatar>
            <Box className={classes.optionTextBlock}>
              <Typography className={classes.optionName} variant="body2" component="span" display="block">
                <span className={option?.object?.isInactive ? classes.optionNameInactive : ''}>
                  {option?.object?.label}
                </span>
              </Typography>
              <Typography className={classes.optionRole} variant="caption" component="span">
                {option?.object?.role ?? '-'}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={value?.length ? '' : placeholder}
            variant="outlined"
            size="small"
            InputProps={{
              ...params.InputProps,
              className: `${classes.inputRoot} ${params.InputProps?.className ?? ''}`.trim(),
              startAdornment: (
                <>
                  {(value ?? []).map((recipient, index) => {
                    const isInactive = !!recipient?.object?.isInactive;
                    const chip = (
                      <Chip
                        key={recipient?.object?.empId ?? index}
                        size="small"
                        label={recipient?.object?.label}
                        onDelete={disabled ? undefined : () => handleRemoveChip(recipient?.object?.empId)}
                        {...(isInactive ? { deleteIcon: <CancelIcon fontSize="small" /> } : {})}
                        className={`${classes.chip} ${isInactive ? classes.chipInactive : ''}`}
                      />
                    );
                    return isInactive ? (
                      <Tooltip
                        key={recipient?.object?.empId ?? index}
                        title={t('notification.createTask.newDesign.inactiveUserTooltip')}
                        arrow
                        placement="bottom"
                        classes={{
                          tooltip: classes.inactiveTooltip,
                          arrow: classes.inactiveTooltipArrow,
                        }}
                      >
                        <span>{chip}</span>
                      </Tooltip>
                    ) : (
                      chip
                    );
                  })}
                </>
              ),
              endAdornment: params.InputProps.endAdornment,
            }}
            inputProps={{
              ...params.inputProps,
              className: classes.input,
            }}
          />
        )}
      />
    </div>
  );
};
